import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import * as uuid from 'uuid';
import {
  OfflineInputsTable,
  OfflineInputTabs,
  OutcomeDataEntry,
  OutcomeDataTable
} from '@blisspointmedia/bpm-types/dist/OfflineInputs';
import { SetError } from '../../../redux/modals';
import * as UserRedux from '../../../redux/user';
import { AddOutcomeDataModal } from './AddOutcomeDataModal';
import { InputContainer } from './InputContainer';
import { InputTable, InputTableHeader, getTablesContainerInnerWidth } from './InputTable';
import {
  CommitSuccessDialog,
  DiscardConfirmationDialog,
  InstructionsDialog,
  ValidationErrorDialog
} from './OfflineInputsDialogs';
import {
  EntryEditsMap,
  NewTableEntriesMap,
  prepCreateTableReq,
  prepDeleteTableReq,
  QueuedRequest,
  TableEditsMap,
  scrollToRow,
  tabularOfflineInputsDownload,
  validateAndUpdateTables as validateAndUpdateTablesInternal
} from './OfflineInputsUtils';

interface OutcomeDataProps {
  inputType: keyof typeof OfflineInputTabs;
  originalTables: OutcomeDataTable[];
  updated: { by?: string; at?: string };
  fetching: boolean;
  fetchFailed: boolean;
  refetch: () => void;
  editingUnlocked: boolean;
  setEditingUnlocked: Dispatch<SetStateAction<boolean>>;
  hasUnsavedChanges: boolean;
  setHasUnsavedChanges: Dispatch<SetStateAction<boolean>>;
  dispatchStateReset: boolean;
  setDispatchStateReset: Dispatch<SetStateAction<boolean>>;
  company: string;
  setError: SetError;
}

export const OutcomeData = ({
  inputType,
  originalTables,
  updated,
  fetching,
  fetchFailed,
  refetch,
  editingUnlocked,
  setEditingUnlocked,
  hasUnsavedChanges,
  setHasUnsavedChanges,
  dispatchStateReset,
  setDispatchStateReset,
  company,
  setError
}: OutcomeDataProps) => {
  const [tables, setTables] = useState<OutcomeDataTable[]>([...originalTables]);
  const [submitting, setSubmitting] = useState(false);
  const [tablesInEditMode, setTablesInEditMode] = useState<string[]>([]);
  const [newTableEntries, setNewTableEntries] = useState<NewTableEntriesMap>({});
  const [validationErrors, setValidationErrors] = useState<{ tableId: string; errors: string[] }[]>(
    []
  );
  const [queuedRequests, setQueuedRequests] = useState<QueuedRequest[]>([]);
  const [deletedTables, setDeletedTables] = useState<OutcomeDataTable[]>([]);
  const [showAddOutcomeDataModal, setShowAddOutcomeDataModal] = useState(false);
  const [showCommitSuccessDialog, setShowCommitSuccessDialog] = useState(false);
  const [showInstructionsDialog, setShowInstructionsDialog] = useState(false);
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  const [tableEdits, setTableEdits] = useState<TableEditsMap>({});
  const [filter, setFilter] = useState<{ filter: (line: Record<string, any>) => boolean }>();
  const userFullName: string = useSelector(UserRedux.fullNameSelector) ?? '';

  const startEditingTable = (tableId: string, blanks: OutcomeDataEntry[]) => {
    setTablesInEditMode([...tablesInEditMode, tableId]);
    setTableEdits({ ...tableEdits, [tableId]: {} });
    setNewTableEntries({ ...newTableEntries, [tableId]: blanks });

    if (blanks.length > 0) {
      scrollToRow(tableId, blanks[blanks.length - 1].date);
    }
  };

  const resetState = useCallback(() => {
    setTables([...originalTables]);
    setSubmitting(false);
    setEditingUnlocked(false);
    setTablesInEditMode([]);
    setHasUnsavedChanges(false);
    setNewTableEntries({});
    setValidationErrors([]);
    setQueuedRequests([]);
    setDeletedTables([]);
    setShowAddOutcomeDataModal(false);
    setShowCommitSuccessDialog(false);
    setShowInstructionsDialog(false);
    setShowDiscardDialog(false);
    setTableEdits({});
    setFilter(undefined);
  }, [originalTables, setEditingUnlocked, setHasUnsavedChanges]);

  useEffect(() => {
    if (dispatchStateReset) {
      resetState();
      setDispatchStateReset(false);
    }
  }, [dispatchStateReset, resetState, setDispatchStateReset]);

  useEffect(() => {
    setTables([...originalTables]);
  }, [originalTables]);

  useEffect(() => {
    setHasUnsavedChanges(Boolean(tablesInEditMode.length) || Boolean(deletedTables.length));
  }, [tablesInEditMode, deletedTables, setHasUnsavedChanges]);

  const onCommitSuccess = () => {
    refetch();
    resetState();
    setShowCommitSuccessDialog(true);
  };

  const validateAndUpdateTables = async () => {
    setSubmitting(true);
    await validateAndUpdateTablesInternal(
      company,
      userFullName,
      inputType,
      tableEdits,
      newTableEntries,
      setValidationErrors,
      setError,
      onCommitSuccess,
      queuedRequests,
      setQueuedRequests
    );
    setSubmitting(false);
  };

  const deleteTable = (tableId: string) => {
    prepDeleteTableReq<OfflineInputsTable, TableEditsMap, NewTableEntriesMap>(
      tableId,
      company,
      userFullName,
      inputType,
      tables,
      setTables as Dispatch<SetStateAction<OfflineInputsTable[]>>,
      deletedTables,
      setDeletedTables as Dispatch<SetStateAction<OfflineInputsTable[]>>,
      queuedRequests,
      setQueuedRequests,
      newTableEntries,
      setNewTableEntries,
      tableEdits,
      setTableEdits
    );
  };

  const download = useCallback(
    () => tabularOfflineInputsDownload(inputType, tables),
    [inputType, tables]
  );

  return (
    <InputContainer
      dialogs={
        <>
          <AddOutcomeDataModal
            show={showAddOutcomeDataModal}
            handleClose={() => setShowAddOutcomeDataModal(false)}
            createTable={(kpiName, kpiDescription, unitType, startDate, endDate) => {
              const newTable: OutcomeDataTable = {
                tableId: uuid.v4(),
                kpiName,
                kpiDescription,
                unitType,
                hasEndDate: Boolean(endDate),
                entries: []
              };

              prepCreateTableReq<OfflineInputsTable, TableEditsMap, NewTableEntriesMap>({
                company,
                updatedBy: userFullName,
                inputType: OfflineInputTabs.OUTCOME_DATA,
                startDate,
                endDate,
                newTable,
                queuedRequests,
                setQueuedRequests,
                tables,
                setTables: setTables as Dispatch<SetStateAction<OfflineInputsTable[]>>,
                tablesInEditMode,
                setTablesInEditMode,
                newTableEntries,
                setNewTableEntries,
                tableEdits,
                setTableEdits
              });
              setEditingUnlocked(true);
            }}
          />
          <CommitSuccessDialog
            show={showCommitSuccessDialog}
            onHide={() => setShowCommitSuccessDialog(false)}
          />
          <InstructionsDialog
            show={showInstructionsDialog}
            onHide={() => {
              setShowInstructionsDialog(false);
            }}
          />
          <DiscardConfirmationDialog
            show={showDiscardDialog}
            onDiscard={() => resetState()}
            onHide={() => setShowDiscardDialog(false)}
          />
          {validationErrors.length > 0 && (
            <ValidationErrorDialog
              errorDates={validationErrors[0].errors}
              onResolve={() => {
                const remaining = validationErrors.slice(1);
                setValidationErrors(remaining);

                if (remaining.length === 0) {
                  validateAndUpdateTables();
                }
              }}
              onReject={() => {
                setValidationErrors([]);
                setShowInstructionsDialog(true);
                scrollToRow(validationErrors[0].tableId, validationErrors[0].errors[0]);
              }}
              table={tables.find(table => table.tableId === validationErrors[0].tableId)}
              tableEdits={tableEdits[validationErrors[0].tableId]}
              getInputDescription={(table: OfflineInputsTable) =>
                (table as OutcomeDataTable).kpiName
              }
              createEmptyEntry={date => ({
                date,
                volume: undefined
              })}
              normalizeEntry={() => {}}
            />
          )}
        </>
      }
      tables={
        <div
          className='tables-container-inner'
          style={getTablesContainerInnerWidth(OfflineInputTabs.OUTCOME_DATA, tables)}
        >
          {tables?.map(table => (
            <InputTable
              inputType={OfflineInputTabs.OUTCOME_DATA}
              table={table}
              key={table.tableId}
              deleteTable={deleteTable}
              tablesInEditMode={tablesInEditMode}
              startEditingTable={startEditingTable}
              editingUnlocked={editingUnlocked}
              edits={tableEdits[table.tableId] ?? {}}
              setEdits={(edits: EntryEditsMap) =>
                setTableEdits({ ...tableEdits, [table.tableId]: edits })
              }
              newEntries={newTableEntries[table.tableId] ?? []}
              submitting={submitting}
              setError={setError}
              filter={filter ? filter.filter : undefined}
              headerLabels={
                <InputTableHeader name={table.kpiName} description={table.kpiDescription} />
              }
            />
          ))}
        </div>
      }
      inputType={inputType}
      updated={updated}
      isEmpty={tables.length + deletedTables.length === 0}
      fetching={fetching}
      fetchFailed={fetchFailed}
      refetch={refetch}
      submitting={submitting}
      editingUnlocked={editingUnlocked}
      setEditingUnlocked={setEditingUnlocked}
      hasUnsavedChanges={hasUnsavedChanges}
      onFilter={filterFn => {
        setFilter({ filter: filterFn });
      }}
      validateAndUpdateTables={validateAndUpdateTables}
      setShowAddInputModal={setShowAddOutcomeDataModal}
      setShowDiscardDialog={setShowDiscardDialog}
      setError={setError}
      download={download}
    />
  );
};
